import { weekDays } from "../services/constants";
import { weekDayOfIso } from "./timetable";

/**
 * Types and helpers for the Anwesenheitslisten screens
 * (BranchSubScreens/CourseAttendance and AttendanceList). The shapes mirror
 * the v2 routes under `api/attendance/`.
 */

export type AttendanceCourse = {
  id: string;
  title: string;
  courseType: "single-course" | "block-course" | "infinity-course";
  deleted: boolean;
};

export type AttendanceSession = {
  date: string; // "YYYY-MM-DD"
  startTime: string; // "HH:MM"
  endTime: string;
  sessionTitle: string | null;
  listId: string | null; // null until somebody opened the list
  scheduled: boolean; // false: list of a session no longer in the schedule
  presentCount: number;
  rosterCount: number;
};

export type AttendanceSessions = {
  course: AttendanceCourse;
  today: string;
  from: string | null;
  hasEarlier: boolean;
  sessions: Array<AttendanceSession>;
};

export type AttendanceEntry = {
  courseBookingId: string;
  fName: string;
  lName: string;
  present: boolean;
};

export type AttendanceList = {
  listId: string;
  courseId: string;
  courseTitle: string;
  sessionTitle: string | null;
  date: string;
  startTime: string;
  endTime: string;
  presentCount: number;
  rosterCount: number;
  entries: Array<AttendanceEntry>;
};

export type AttendanceSummaryRow = {
  personKey: string;
  fName: string;
  lName: string;
  attended: number;
  total: number;
};

/** URL segment of a session: "2026-10-08_1800". */
export const sessionKey = (date: string, startTime: string) =>
  `${date}_${startTime.replace(":", "")}`;

/** Inverse of `sessionKey`, null for anything else. */
export const parseSessionKey = (
  key: string
): { date: string; startTime: string } | null => {
  const match = /^(\d{4}-\d{2}-\d{2})_(\d{2})(\d{2})$/.exec(key || "");
  if (!match) return null;
  return { date: match[1], startTime: `${match[2]}:${match[3]}` };
};

/** "Mittwoch, 08.10.2026" */
export const sessionDateLabel = (iso: string) => {
  const [year, month, day] = iso.split("-");
  return `${weekDays[weekDayOfIso(iso)].name}, ${day}.${month}.${year}`;
};

/** Attendance rate in whole percent, 0 without any list. */
export const attendanceRate = (attended: number, total: number) =>
  total > 0 ? Math.round((attended / total) * 100) : 0;

/** "3 / 12 anwesend", or a hint while there is no list yet. */
export const sessionStatusLabel = (session: AttendanceSession) =>
  session.listId === null
    ? "Noch keine Liste"
    : `${session.presentCount} / ${session.rosterCount} anwesend`;

/** Rows for the CSV export of the course report. */
export const summaryCsvRows = (rows: Array<AttendanceSummaryRow>) =>
  rows.map((row) => ({
    Vorname: row.fName,
    Nachname: row.lName,
    Anwesend: row.attended,
    Termine: row.total,
    "Quote (%)": attendanceRate(row.attended, row.total),
  }));
